import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import jwt_decode from 'jwt-decode';
import useUser from '../hooks/useUser';
import useHttp from '../hooks/useHttp';
import { toast } from 'react-toastify';
import CountContext from './countContext';
import { hashCardId } from '../util/hashUtils';
import { getDeviceDescriptor, requestDeviceTokenStorageKey } from '../util/deviceInfo';
import { isFeatureEnabled } from '../config/featureFlags';

const SESSION_TIME_REMAINING = 180; // 3 minutes in seconds (TODO move to config?)

let logoutTimer;
let sessionTimer;

export const isDevEnvironment = Boolean(import.meta.env?.DEV) || import.meta.env?.VITE_IS_DEV === 'true';

// Stable identity for "no counters deactivated" so consumers memoized on the set
// (e.g. the sidebar) don't re-render when nothing is hidden (the common case).
const EMPTY_DEACTIVATED_COUNTER_KEYS = new Set();

// Menu links that are never rendered as their own sidebar entry because their
// content now lives inside another, unified view. The issue/outage IT
// statistics ("/issues/statistics", grouped under "Mängel & Störungen") are
// folded into the single "Statistiken → IT-Statistik" view as a tab, so the
// standalone entry is suppressed to avoid listing "IT-Statistik" twice. This is
// a no-op on branches that do not define the standalone entry.
const SUPPRESSED_MENU_LINKS = new Set(['/issues/statistics']);

// Counters that must never be hidden, no matter what a user stored in their
// preferences. Live food orders (ordered on site via QR code) always have to raise
// the sidebar attention stripes, so the kitchen counter cannot be switched off.
// The backend mirrors this by not generating a `deactivateCounter:<key>` right for
// these keys (see `util/counter-keys.js`).
export const NON_DEACTIVATABLE_COUNTER_KEYS = new Set(['openFoodOrders']);

export const availableMenuItems = [
  {
    name: 'Buchungsübersicht',
    link: '/bookings',
    needsRights: ['booking:read:assigned', 'internalBooking:read:own'],
  },
  {
    name: 'Handlungsbedarf',
    link: '/pending-bookings',
    needsRights: ['internalBooking:read:own'],
    countUrl: '/api/v1/internalBooking/pending/count',
    countKey: 'pendingBookings',
    autoStartCount: true,
  },
  {
    // Visible to every authenticated user; individual features inside the view
    // are gated per user by the backend (requiredRights in the changelog).
    name: 'Neue Funktionen',
    link: '/new-features',
    countUrl: '/api/v1/changelog/new-features/unseen-count',
    countKey: 'newFeaturesUnseen',
    autoStartCount: true,
  },
  {
    name: 'Aufgaben',
    link: '/todos',
    needsRights: ['todo:read', 'todo:read:own'],
    countUrl: '/api/v1/todo/count',
    countKey: 'todo',
    autoStartCount: true,
  },
  {
    name: 'Dev-Todos',
    link: '/dev-todos',
    needsRights: ['dev:todo:read'],
    countUrl: '/api/v1/todo/dev-count',
    countKey: 'devTodo',
    autoStartCount: true,
  },
  {
    name: 'Issue-Board',
    link: '/issues',
    needsRights: ['issue:read', 'issue:read:own'],
    countUrl: '/api/v1/issue/count',
    countKey: 'openIssues',
    autoStartCount: true,
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['jira', 'ticket', 'defekt', 'störung', 'mangel'],
  },
  {
    name: 'Issue melden',
    link: '/issues/report',
    needsRights: ['issue:create'],
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['melden', 'defekt', 'kaputt'],
  },
  {
    name: 'Ausfall-Board',
    link: '/outages',
    needsRights: ['outage:read', 'outage:read:own'],
    countUrl: '/api/v1/outage/count',
    countKey: 'activeOutages',
    autoStartCount: true,
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['ausfall', 'nicht bespielbar', 'blocker', 'westen'],
  },
  {
    name: 'Raum-Ampel',
    link: '/roomStatus',
    needsRights: ['issue:read', 'issue:read:own', 'outage:read', 'outage:read:own'],
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['ampel', 'status', 'räume'],
  },
  {
    name: 'Präsentation',
    link: '/issues/presentation',
    needsRights: ['issue:read'],
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['präsentation', 'vollbild', 'monitor'],
  },
  {
    name: 'Eskalations-Audit',
    link: '/issues/escalations',
    needsRights: ['issue:manage', 'issue:statistics'],
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['eskalation', 'sms', 'audit'],
  },
  {
    name: 'QR-Codes',
    link: '/issues/qr-codes',
    needsRights: ['issue:configure', 'issue:manage'],
    groupPath: 'Mängel & Störungen',
    menuSearchTerms: ['qr', 'raumcodes', 'drucken'],
  },
  {
    // Merged kitchen overview: pizza bookings + live online food orders.
    // The counter tracks live food orders (ordered on site via QR code) that still
    // need kitchen action and always raises the sidebar attention stripes; it is
    // listed in NON_DEACTIVATABLE_COUNTER_KEYS and can therefore not be hidden.
    name: 'Küche',
    link: '/kitchen',
    needsRights: ['internalBooking:read:own', 'foodOrder:read', 'foodOrder:read:own'],
    groupPath: 'Speisen',
    countUrl: '/api/v1/foodOrder/kitchen/open/count',
    countKey: 'openFoodOrders',
    autoStartCount: true,
  },
  {
    name: 'Speisekarte',
    link: '/menuItems',
    needsRights: ['menuItem:assign', 'menuItem:assign:own'],
    groupPath: 'Speisen',
  },
  {
    // QR codes for the online menu: a general code per location plus, where
    // tables are configured, one per table (invisible table assignment).
    name: 'Bestell-QR-Codes',
    link: '/orderQrCodes',
    needsRights: ['foodOrder:read', 'foodOrder:read:own'],
    groupPath: 'Speisen',
  },
  {
    // Room layout per location: where the tables stand, plus walls, doors and
    // free text labels. Drawn by the branch manager, read by everyone else.
    name: 'Tischplan',
    link: '/tablePlan',
    needsRights: ['tablePlan:read', 'tablePlan:read:own'],
    groupPath: 'Speisen',
  },
  {
    name: 'Reservierungsverwaltung',
    link: '/temporaryReservationCleanup',
    needsRights: ['temporaryReservation:special:clear'],
    groupPath: 'Kapazität',
  },
  {
    name: 'Blocker-Verwaltung',
    link: '/blockerManagement',
    needsRights: ['internalBooking:special:blockDirect', 'internalBooking:special:blockDirectOwn'],
    groupPath: 'Kapazität',
    countUrl: '/api/v1/internalBooking/blockerRequest/count',
    countKey: 'blockerRequests',
    autoStartCount: true,
  },
  {
    name: 'Testbuchungen',
    link: '/test-booking-cleanup',
    needsRights: ['internalBooking:special:cleanupTestBookings'],
    groupPath: 'Kapazität',
  },
  {
    name: 'Cache-Verwaltung',
    link: '/capacityCacheCleanup',
    needsRights: ['internalBooking:special:clearDayCache'],
    groupPath: 'Kapazität',
  },
  {
    name: 'Regiondo Migration',
    link: '/regiondo-migration',
    needsRights: ['internalBooking:migration:regiondo'],
    groupPath: 'IT',
  },
  {
    name: 'Regiondo Voucher Migration',
    link: '/regiondo-voucher-migration',
    needsRights: ['internalBooking:migration:regiondo'],
    groupPath: 'IT',
  },
  {
    name: 'Regiondo Wertgutschein Import',
    link: '/regiondo-value-voucher-import',
    needsRights: ['voucher:special:regiondoValueVoucherImport'],
    groupPath: 'IT',
  },
  {
    name: 'Old Bookings Import',
    link: '/old-booking-import',
    needsRights: ['internalBooking:migration:regiondo'],
    groupPath: 'IT',
  },
  {
    name: 'Notfallmodus Druck',
    link: '/emergencyPrintMode',
    needsRights: ['entertainment:special:emergencyPrintMode'],
    groupPath: 'IT',
  },
  {
    name: 'Auslastung',
    link: '/resources',
    needsRights: ['internalBooking:read:resources'],
    groupPath: 'Kapazität',
  },
  {
    name: 'Gutscheinverwaltung',
    link: '/voucherSearch',
    needsRights: ['voucher:read:own'],
    groupPath: 'Gutscheine',
  },
  {
    name: 'Zahlungseingang Gutscheine (Bank)',
    link: '/sellVoucher',
    needsRights: ['voucher:special:sellOfflineVoucher'],
    groupPath: 'Gutscheine',
  },
  {
    name: 'Kulanz-/Marketing-Gutscheine',
    link: '/giftedBalanceVoucher',
    needsRights: ['voucher:special:giftedBalance'],
    groupPath: 'Gutscheine',
  },
  {
    name: 'Freigaben Kulanz-/Marketing-Gutscheine',
    link: '/giftedBalanceVoucherApprovals',
    needsRights: [
      'voucher:special:giftedBalanceApprove',
      'customerExperienceFeedback:special:approveHighValueVoucher',
    ],
    groupPath: 'Gutscheine',
    countUrl: '/api/v1/voucher/gifted-balance/requests/count',
    countKey: 'giftedBalanceVoucherApprovals',
    autoStartCount: true,
  },
  {
    name: 'Freispiel-Flextickets',
    link: '/freeGameFlexticket',
    needsRights: ['voucher:special:freeGameFlexticket'],
    groupPath: 'Gutscheine',
  },
  {
    name: 'Gutschein einlösen',
    link: '/voucherCheck',
    needsRights: ['voucher:redeem:read'],
    groupPath: 'Gutscheine',
  },
  {
    name: 'Einlösehistorie',
    link: '/voucherRedemptionHistory',
    needsRights: ['voucher:special:redemptionHistory'],
    groupPath: 'Gutscheine',
  },
  // TODO only show for Lasertag
  {
    name: 'Westenraum',
    link: '/operator',
    needsRights: ['lasertagGame:read'],
    groupPath: 'Lasertag',
  },
  {
    name: 'Briefings',
    link: '/briefings',
    needsRights: ['internalBooking:special:briefing'],
    countUrl: '/api/v1/internalBooking/briefings/open/count',
    countKey: 'briefingsOpen',
    autoStartCount: true,
    groupPath: 'Lasertag',
  },
  {
    name: 'Spielauswertung',
    link: '/lasertagStats',
    needsRights: ['statistic:read:own'],
    groupPath: 'Lasertag',
  },
  {
    name: 'Axe Waiver',
    link: '/waivers',
    needsRights: ['disclaimer:read:own'],
    groupPath: 'Axtwerfen',
    visibleIf: ctx => ctx?.capabilities?.hasAxeInAssignedBranchesOrGlobal === true,
  },
  {
    name: 'Rückrufe',
    link: '/calls',
    needsRights: ['call:read:own'],
    countUrl: '/api/v1/call/count',
    countKey: 'calls',
    autoStartCount: false,
    groupPath: 'Kommunikation',
  },
  {
    name: 'KI-Rückrufe',
    link: '/agent-callbacks',
    needsRights: ['agentCallback:read'],
    countUrl: '/api/v1/agentCallback/counts',
    countKey: 'agentCallbacks',
    autoStartCount: true,
    attention: true,
    groupPath: 'Kommunikation',
  },
  {
    name: 'E-Mails',
    link: '/mails',
    needsRights: ['mail:read:own'],
    countUrl: '/api/v1/mail/count',
    countKey: 'mails',
    autoStartCount: true,
    groupPath: 'Kommunikation',
    menuSearchTerms: ['email', 'e-mail'],
  },
  // Call protocols only exist while the feature flag is enabled
  ...(isFeatureEnabled('callProtocol')
    ? [
        {
          name: 'Gesprächsprotokolle',
          link: '/callProtocols',
          needsRights: ['callProtocol:read'],
          groupPath: 'Kommunikation',
          menuSearchTerms: ['protokoll', 'anruf', 'gespräch', 'notiz', 'call'],
        },
      ]
    : []),
  // Chat-agent entry only exists while the feature flag is enabled — and, like the chat agent in
  // the backend, only for holders of preview:aiAgents (the routes answer 404 to everyone else).
  ...(isFeatureEnabled('agentChat')
    ? [
        {
          name: 'Eskalierte Chats',
          link: '/escalated-chats',
          needsRights: ['agent:escalated', 'preview:aiAgents'],
          needsAllRights: true,
          countUrl: '/api/v1/agent/escalated/counts',
          countKey: 'escalatedChats',
          autoStartCount: true,
          groupPath: 'Kommunikation',
        },
      ]
    : []),
  {
    // Available to every authenticated employee — no dedicated right needed.
    name: 'Mitarbeiter-Chat',
    link: '/mitarbeiter-chat',
    countUrl: '/api/v1/employeeChat/unread-count',
    countKey: 'employeeChatUnread',
    autoStartCount: true,
    groupPath: 'Kommunikation',
    menuSearchTerms: ['chat', 'nachricht', 'messenger', 'kollegen', 'mitarbeiter'],
  },
  {
    name: 'Agent Sessions Statistik',
    link: '/agent-session-stats',
    needsRights: ['agent:stats'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Gemeldete Konversationen',
    link: '/agent-reported',
    needsRights: ['agent:reported'],
    groupPath: 'Kommunikation',
  },
  {
    name: 'Agenten-Chat Audit',
    link: '/agent-chat-audit',
    needsRights: ['agent:audit'],
    groupPath: 'Kommunikation',
  },

  {
    name: 'Gesendete SMS',
    link: '/smsLog',
    needsRights: ['sentShortMessage:read'],
    groupPath: 'Kommunikation',
  },
  {
    name: 'Benutzer',
    link: '/userManagement',
    needsRights: ['user:read', 'user:special:manageCcEmployees'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Mitarbeiter-Stammdaten',
    link: '/employeeBaseData',
    needsRights: ['user:update:baseData'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Mitarbeiterstatus',
    link: '/employeeStatus',
    needsRights: ['user:read:own'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Mitarbeiter aktivieren',
    link: '/timebutlerConversion',
    needsRights: ['user:read:own', 'user:special:manageCcEmployees'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Mitarbeiterausweise',
    link: '/cardQueue',
    needsRights: ['user:special:print', 'user:special:printAssignedBranches'],
    countUrl: '/api/v1/user/cardqueue/count',
    countKey: 'cardQueue',
    autoStartCount: true,
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Mitarbeiterkarten-Rohlinge',
    link: '/employeeCardStock',
    needsRights: ['branch:special:manageEmployeeCardStock'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'CSV Sync-Import',
    link: '/specialSyncImport',
    needsRights: ['user:special:syncImport'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Rollen',
    link: '/roleManagement',
    needsRights: ['role:read'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Rechte-Übersicht',
    link: '/roleRightsOverview',
    needsRights: ['role:read', 'user:read'],
    needsAllRights: true,
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Audit-Log',
    link: '/auditLog',
    needsRights: ['auditLog:read'],
    groupPath: 'Verwaltung/Sicherheit',
  },
  {
    name: 'IP-Audit',
    link: '/suspiciousRequestAudit',
    needsRights: ['securityAudit:read'],
    countUrl: '/api/v1/suspiciousRequestIncident/count',
    countKey: 'suspiciousRequestIncidents',
    autoStartCount: true,
    attention: true,
    groupPath: 'Verwaltung/Sicherheit',
  },
  // AirKey entry only exists while the feature flag is enabled
  ...(isFeatureEnabled('airkey')
    ? [
        {
          name: 'AirKey',
          link: '/airkey',
          needsRights: ['airkey:read'],
          groupPath: 'Verwaltung/Sicherheit',
        },
      ]
    : []),
  {
    name: 'Gerätetyp-Anfragen',
    link: '/deviceTypeRequests',
    needsRights: ['deviceTypeRequest:approve'],
    countUrl: '/api/v1/deviceTypeRequest/count',
    countKey: 'deviceTypeRequests',
    autoStartCount: true,
    attention: true,
    groupPath: 'Verwaltung/Sicherheit',
  },
  {
    name: 'KI-Vorschläge',
    link: '/aiProposals',
    needsRights: ['aiProposal:read'],
    groupPath: 'Verwaltung/KI',
  },
  {
    name: 'KI-Audit',
    link: '/aiAudit',
    needsRights: ['aiProposal:read'],
    groupPath: 'Verwaltung/KI',
  },
  {
    name: 'Department-Rollen',
    link: '/departmentRoleMapping',
    needsRights: ['departmentRoleMapping:read'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Recruitee Bewerber',
    link: '/recruitee',
    needsRights: ['recruitee:read:own'],
    countUrl: '/api/v1/recruitee/applicants/overdue/count',
    countKey: 'recruiteeOverdueApplicants',
    autoStartCount: true,
    attention: true,
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Schulungs-Regeln',
    link: '/memberspotTrainingManagement',
    needsRights: ['memberspotTraining:update'],
    groupPath: 'Verwaltung/Schulungen',
  },
  {
    name: 'Schulungsstand',
    link: '/memberspotTrainingStatus',
    needsRights: ['memberspotTraining:read:own'],
    countUrl: '/api/v1/memberspot-training/assignments/count',
    countKey: 'memberspotTrainingDue',
    autoStartCount: true,
    attention: true,
    groupPath: 'Verwaltung/Schulungen',
  },
  {
    name: 'Meine Schulungen',
    link: '/myTrainings',
    countUrl: '/api/v1/memberspot-training/me/count',
    countKey: 'memberspotTrainingSelf',
    autoStartCount: true,
    attention: true,
    groupPath: 'Verwaltung/Schulungen',
  },
  {
    name: 'Gruppen',
    link: '/groupManagement',
    needsRights: ['group:read'],
    groupPath: 'Verwaltung/Mitarbeiter',
  },
  {
    name: 'Geschäftskunden',
    link: '/customerManagement',
    needsRights: ['customer:read'],
    groupPath: 'Verwaltung/Kunden',
  },
  {
    name: 'CRM (B2B)',
    link: '/crm',
    needsRights: ['crm:read'],
    groupPath: 'Verwaltung/Kunden',
  },
  {
    name: 'Mitglieder',
    link: '/memberManagement',
    needsRights: ['member:read'],
    groupPath: 'Verwaltung/Kunden',
  },
  {
    name: 'Spielernamen-Blacklist',
    link: '/playerNameBlacklist',
    needsRights: ['playerNameBlacklist:manage', 'playerNameBlacklist:suggest'],
    groupPath: 'Verwaltung/Kunden',
  },
  {
    name: 'Filialen',
    link: '/branchManagement',
    needsRights: ['branch:read'],
    groupPath: 'Verwaltung/Filialstruktur',
  },
  {
    name: 'Entertainments',
    link: '/entertainmentManagement',
    needsRights: ['entertainment:read'],
    groupPath: 'Verwaltung/Filialstruktur',
  },
  {
    name: 'GetResponse Marketing',
    link: '/getResponseMarketing',
    needsRights: ['getresponse:read'],
    groupPath: 'Verwaltung/Filialstruktur',
  },
  {
    name: 'Online-Kunden',
    link: '/onlineCustomers',
    needsRights: ['onlineCustomer:read'],
    groupPath: 'Verwaltung/Kunden',
  },
  // The loyalty programme configures the stamp card and the wheel — the part of the customer
  // account that hands something out. It follows that switch, not the account's: the account runs
  // in live, the programme does not, so this entry must not appear there.
  ...(isFeatureEnabled('loyaltyProgramme')
    ? [
        {
          name: 'Treueprogramm',
          link: '/loyaltyProgram',
          needsRights: ['loyalty:manage'],
          groupPath: 'Verwaltung/Kunden',
        },
      ]
    : []),
  {
    name: 'Entertainment-Typen',
    link: '/entertainmentTypeManagement',
    needsRights: ['entertainmentType:read'],
    groupPath: 'Verwaltung/Filialstruktur',
  },
  {
    name: 'Rechtsträger',
    link: '/legalEntityManagement',
    needsRights: ['legalEntity:read'],
    groupPath: 'Verwaltung/Filialstruktur',
  },
  {
    name: 'Öffnungszeiten',
    link: '/openingHourManagement',
    needsRights: ['openingHour:read'],
    groupPath: 'Verwaltung/Verfügbarkeiten',
  },
  {
    name: 'Öffnungszeiten-Übersicht',
    link: '/openingHoursOverview',
    needsRights: [
      'openingHour:read',
      'googleBusiness:read',
      'openingHour:special:updateAssignedBranch',
    ],
    groupPath: 'Verwaltung/Verfügbarkeiten',
  },
  {
    name: 'Spezialtage',
    link: '/specialDayManagement',
    needsRights: ['specialDay:read'],
    groupPath: 'Verwaltung/Verfügbarkeiten',
  },
  {
    name: 'Verügbarkeiten',
    link: '/availabilityManagement',
    needsRights: ['availability:read'],
    groupPath: 'Verwaltung/Verfügbarkeiten',
  },
  {
    name: 'PG-Verfügbarkeiten',
    link: '/resourceVoucherAvailabilityManagement',
    needsRights: ['resourceVoucherAvailability:update'],
    groupPath: 'Verwaltung/Verfügbarkeiten',
  },
  {
    name: 'Produkte',
    link: '/productManagement',
    needsRights: ['product:read'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Preisübersicht',
    link: '/priceOverview',
    needsRights: ['product:read', 'product:read:priceOverviewOwn'],
    groupPath: 'Verwaltung/Angebot',
    menuSearchTerms: ['preise', 'preisliste', 'preisübersicht', 'price'],
  },
  {
    name: 'Booking-Widgets',
    link: '/bookingWidgetManagement',
    needsRights: ['bookingWidget:read'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Ressourcen',
    link: '/resourceManagement',
    needsRights: ['resource:update'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Upgrades',
    link: '/upgradeManagement',
    needsRights: ['upgrade:read'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Gutscheinprodukte',
    link: '/voucherProductManagement',
    needsRights: ['voucherProduct:read'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Rechnungen',
    link: '/invoiceManagement',
    needsRights: ['invoice:read:own'],
    groupPath: 'Verwaltung/Buchhaltung',
  },
  {
    name: 'Kassenbuch konvertieren',
    link: '/copagoCashbook',
    needsRights: ['accounting:special:tools'],
    groupPath: 'Verwaltung/Buchhaltung',
  },
  {
    name: 'Gutschein Monatssumme',
    link: '/voucherMonthlySummary',
    needsRights: ['accounting:special:tools'],
    groupPath: 'Verwaltung/Buchhaltung',
  },
  {
    name: 'PG-Produkte',
    link: '/resourceVoucherProductManagement',
    needsRights: ['resourceVoucherProduct:update'],
    groupPath: 'Verwaltung/Angebot',
  },
  {
    name: 'Bon-Liste',
    link: '/bonList',
    needsRights: ['bon:read:own'],
    groupPath: 'Verwaltung/Copago',
  },
  {
    name: 'Copago Warengruppen',
    link: '/copagoGroupManagement',
    needsRights: ['copagoGroup:read'],
    groupPath: 'Verwaltung/Copago',
  },
  {
    name: 'Copago Artikel',
    link: '/copagoArticleManagement',
    needsRights: ['copagoArticle:read'],
    groupPath: 'Verwaltung/Copago',
    countUrl: '/api/v1/copagoArticle/priceDiscrepancies/count',
    countKey: 'copagoPriceDiscrepancies',
    autoStartCount: true,
  },
  {
    name: 'Mail-Templates',
    link: '/mailTemplateManagement',
    needsRights: ['mailTemplate:read'],
    groupPath: 'Kommunikation',
  },
  {
    name: 'Massenversand',
    link: '/bulk-communication',
    needsRights: ['bulkCommunication:send'],
    groupPath: 'Kommunikation',
  },
  {
    name: 'E-Mail-Marketing',
    link: '/emailMarketing',
    needsRights: ['emailMarketing:read'],
    groupPath: 'Marketing',
    menuSearchTerms: ['newsletter', 'birthday', 'geburtstag', 'flow', 'email'],
  },
  {
    name: 'Rabattcodes',
    link: '/discountCodeManagement',
    needsRights: ['discountCode:read'],
    groupPath: 'Marketing',
  },
  {
    name: 'Meine Rabattcodes',
    link: '/employeeDiscountCodes',
    needsRights: ['discountCode:read:own'],
    groupPath: 'Marketing',
  },
  {
    name: 'Printserver konfigurieren',
    link: '/printserverConfig',
    needsRights: ['printserver:configure'],
    groupPath: 'IT',
  },
  {
    name: 'Notfallmodus TOTP-Setup',
    link: '/emergencyTotp',
    needsRights: ['settings:update'],
    groupPath: 'IT',
  },
  {
    name: 'Copago-Statistik',
    link: '/bonAnalysis',
    needsRights: ['statistic:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Copago-Abgleich (Tool vs. Kasse)',
    link: '/copagoComplianceStatistics',
    needsRights: ['statistic:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Personalkosten-Statistik',
    link: '/staffCostStatistics',
    needsRights: ['statistic:special:staffCost'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Personalkosten (Gesamt / Bereiche)',
    link: '/areaStaffCostStatistics',
    needsRights: ['statistic:special:staffCost'],
    groupPath: 'Statistiken',
    // Hidden behind a feature flag for now — see FEATURE_FLAGS.md.
    visibleIf: () => isFeatureEnabled('staffCostAreaOverview'),
  },
  {
    name: 'B2B-Statistik',
    link: '/b2bStatistics',
    needsRights: ['statistic:read', 'statistic:special:staffCost', 'statistic:read:b2b'],
    groupPath: 'Statistiken',
    visibleIf: () => isFeatureEnabled('b2bStatistics'),
  },
  {
    name: 'IT-Statistik',
    link: '/itStatistics',
    needsRights: [
      'statistic:read',
      'statistic:special:staffCost',
      'issue:statistics',
      'cluster:read',
      'fleet:read',
      'fleet:read:own',
      'printerStatus:read',
      'printerStatus:read:own',
    ],
    groupPath: 'Statistiken',
  },
  {
    name: 'Konkurrenten-Statistik',
    link: '/competitors',
    needsRights: ['competitorAvailability:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Gutscheinstatistik',
    link: '/voucherStatistics',
    needsRights: ['voucher:special:statistics', 'voucher:special:statisticsOwn'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Upgrade-Statistik (Pakete & Upsells)',
    link: '/upgradeStatistics',
    needsRights: ['upgrade:special:statistics', 'upgrade:special:statisticsOwn'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Upsell-Buchungen Ausblick',
    link: '/upcomingUpgradeBookings',
    needsRights: ['upgrade:special:upcomingOverview'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Rabattcode-Statistik',
    link: '/discountCodeStatistics',
    needsRights: ['discountCode:special:statistics'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Freigetränke-Statistik',
    link: '/freeDrinksStatistics',
    needsRights: ['employeeFreeDrinks:statistics:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Feedback-Auswertung',
    link: '/feedback-auswertung',
    needsRights: ['customerExperienceFeedback:read', 'customerExperienceFeedback:read:own'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Umfrage-Statistik',
    link: '/surveyStatistics',
    needsRights: ['survey:statistics'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['umfrage', 'survey', 'feedback', 'antworten'],
  },
  {
    name: 'Verbandbuch-Auswertung',
    link: '/verbandbuchStatistics',
    needsRights: ['verbandbuch:read', 'verbandbuch:read:own'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['erste hilfe', 'unfall', 'verletzung'],
  },
  {
    name: 'Frontend-Migration',
    link: '/frontendMigrationStats',
    needsRights: ['frontendUsage:statistics:read'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['migration', 'neues frontend', 'altes frontend', 'usage', 'nutzung'],
  },
  {
    name: 'Demografie-Statistik',
    link: '/demographyStats',
    needsRights: ['statistic:read', 'statistic:read:demographyOwn'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Self Service-Statistik',
    link: '/selfServiceStats',
    needsRights: ['statistic:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Call Center-Statistik',
    link: '/callCenterStats',
    needsRights: ['statistic:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'IT-Statistik',
    link: '/issues/statistics',
    needsRights: ['issue:statistics'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['statistik', 'reaktionszeit', 'downtime', 'issues', 'störung'],
  },
  {
    name: 'Buchungsstatistik',
    link: '/bookingOriginStats',
    needsRights: ['statistic:read', 'statistic:read:bookingOriginOwn'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Widget Conversion-Statistik',
    link: '/widgetConversionAnalytics',
    needsRights: ['widgetAnalytics:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'A/B-Tests',
    link: '/experiments',
    needsRights: ['experiment:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Customer Journey',
    link: '/customerJourney',
    needsRights: ['statistic:read', 'statistic:read:customerJourneyOwn'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Beschwerdestatistik',
    link: '/complaintStatistics',
    needsRights: ['statistic:read', 'statistic:read:complaintsOwn'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['beschwerden', 'feedback', 'kulanz', 'gutschein', 'antwortzeit', 'reklamation'],
  },
  {
    name: 'Marketing-Kosten (Statistik)',
    link: '/marketingCostStatistics',
    needsRights: ['marketing:read', 'statistic:read'],
    groupPath: 'Statistiken',
    menuSearchTerms: ['google', 'ads', 'werbekosten', 'roas', 'kampagnen'],
  },
  {
    name: 'Marketing-Kosten erfassen',
    link: '/marketingCosts',
    needsRights: ['marketing:read'],
    groupPath: 'Marketing',
  },
  {
    name: 'Buchungsstatistik (alte Auswertung)',
    link: '/stats',
    needsRights: ['statistic:read'],
    groupPath: 'Statistiken',
  },
  {
    name: 'Freigetränke',
    link: '/freeDrinks',
  },
  {
    name: 'Verbandbuch',
    link: '/verbandbuch',
    needsRights: ['verbandbuch:create'],
    menuSearchTerms: ['erste hilfe', 'unfall', 'verletzung'],
  },
  {
    name: 'Gespeicherte Anfragen',
    link: '/storedRequests',
    countKey: 'storedRequests',
  },
  {
    name: 'Mail-Factory (Inbox/AI)',
    link: '/mail-factory',
    needsRights: ['dev:tool:mailfactory'],
    groupPath: 'Dev',
    devOnly: true,
  },
  {
    name: 'Time Machine',
    link: '/time-machine',
    needsRights: ['dev:tool:timemachine'],
    groupPath: 'Dev',
    devOnly: true,
  },
  {
    name: 'Dev Issues',
    link: '/dev-issues',
    needsRights: ['dev:todo:read'],
    groupPath: 'Dev',
  },
  {
    name: 'Identität ändern',
    link: '/switch-identity',
    needsRights: ['user:special:switchIdenty'],
    groupPath: 'Dev',
  },
  {
    name: 'Feedback (Admin)',
    link: '/feedback-admin',
    needsRights: ['dev:feedback', 'dev:feedback:readAssigned'],
    countUrl: '/api/v1/feedback/count',
    countKey: 'feedbackOpen',
    autoStartCount: true,
    groupPath: 'Dev',
  },
  {
    name: 'Kundenfeedback',
    link: '/customer-experience-feedback',
    needsRights: ['customerExperienceFeedback:read', 'customerExperienceFeedback:read:own'],
    countUrl: '/api/v1/experienceFeedback/count',
    countKey: 'customerExperienceFeedback',
    autoStartCount: true,
    groupPath: 'Feeback & Reviews',
  },
  {
    name: 'Feedback-Klassifikation',
    link: '/feedback-classification-settings',
    needsRights: ['customerExperienceFeedback:configure'],
    groupPath: 'Feeback & Reviews',
  },
  {
    name: 'Google-Rezensionen',
    link: '/google-reviews',
    needsRights: ['googleReview:read'],
    countUrl: '/api/v1/googleReview/count',
    countKey: 'googleReviewsOpen',
    autoStartCount: true,
    groupPath: 'Feeback & Reviews',
  },
  {
    name: 'Umfragen',
    link: '/surveys',
    needsRights: ['survey:read'],
    groupPath: 'Feeback & Reviews',
    menuSearchTerms: ['umfrage', 'survey', 'popup', 'fragebogen'],
  },
  {
    name: 'Feedback geben',
    link: '/feedback',
    countUrl: '/api/v1/feedback/me/unread-count',
    countKey: 'feedbackUnread',
    autoStartCount: true,
  },
  {
    name: 'Einstellungen',
    link: '/settings',
  },
  {
    name: 'News',
    link: '/newsManagement',
    needsRights: ['news:read'],
    groupPath: 'Verwaltung',
  },
  {
    name: 'Formulare & Links',
    link: '/formsAndLinks',
  },
];

const AuthContext = React.createContext({
  token: '',
  deviceToken: '',
  tempToken: '',
  tempIntermediateToken: '',
  isLoggedIn: false,
  menuItems: [],
  userRights: [],
  expandedUserRights: [],
  intermediateTokens: {},
  sessionTimeRemaining: SESSION_TIME_REMAINING,
  isCounterDevice: false,
  login: (token, data) => {},
  logout: () => {},
  setTempToken: (token, intermediateToken) => {},
  clearTempToken: () => {},
  checkRight: right => {},
  checkRightOnlyInExpanded: right => {},
  checkRightAny: right => {},
  deactivatedCounterKeys: EMPTY_DEACTIVATED_COUNTER_KEYS,
  addIntermediateToken: (cardId, token) => {},
  removeIntermediateToken: cardId => {},
  resetSessionTimer: () => {},
  rfidLogin: (cardId, intermediateToken) => {},
  resetDevice: () => {},
  clearDeviceToken: () => {},
  hasBeenIntroducedTo: null,
  setHasBeenIntroducedTo: () => {},
  userPreferencesHydrated: false,
});

// Helper function to calculate eligible users for a request
const calculateEligibleUsers = (
  request,
  intermediateTokens,
  deviceToken,
  token,
  isCounterDevice
) => {
  const eligibleUsers = [];

  if (isCounterDevice) {
    // For counter devices: check intermediate tokens and device token
    // Check intermediate tokens for users with the required right (current user and other users)
    if (intermediateTokens && Object.keys(intermediateTokens).length > 0) {
      Object.entries(intermediateTokens).forEach(([cardHash, tokenData]) => {
        try {
          const decodedToken = jwt_decode(tokenData.token);

          let hasRightCount = 0;
          const allRights = [
            ...decodedToken.userRights.standard,
            ...decodedToken.userRights.expanded,
          ];
          for (const requiredRight of request.requiredRights) {
            if (
              allRights.some(
                right => requiredRight === right || requiredRight.startsWith(right + ':')
              )
            ) {
              hasRightCount++;
            }
          }

          if (hasRightCount == request.requiredRights.length) {
            eligibleUsers.push({
              name: decodedToken.userName,
              userId: decodedToken.userId,
              cardHash: cardHash,
            });
          }
        } catch (error) {
          console.error('Error decoding intermediate token:', error);
        }
      });
    }

    // Check device token owner
    if (deviceToken && false) {
      // TODO: remove this once we have a way to grant rights to the device token
      try {
        const decodedDeviceToken = jwt_decode(deviceToken);

        if (
          decodedDeviceToken.userId &&
          !eligibleUsers.some(user => user.userId === decodedDeviceToken.userId)
        ) {
          let hasRightCount = 0;
          const allRights = [
            ...decodedDeviceToken.userRights.standard,
            ...decodedDeviceToken.userRights.expanded,
          ];
          for (const requiredRight of request.requiredRights) {
            if (
              allRights.some(
                right => requiredRight === right || requiredRight.startsWith(right + ':')
              )
            ) {
              hasRightCount++;
            }
          }

          if (hasRightCount == request.requiredRights.length) {
            eligibleUsers.push({
              name: decodedDeviceToken.userName,
              userId: decodedDeviceToken.userId,
              cardHash: decodedDeviceToken.cardId,
            });
          }
        }
      } catch (error) {
        console.error('Error decoding device token:', error);
      }
    }
  } else {
    // For company and private devices: only check the current token
    if (token) {
      try {
        const decodedToken = jwt_decode(token);

        let hasRightCount = 0;
        const allRights = [
          ...decodedToken.userRights.standard,
          ...decodedToken.userRights.expanded,
        ];
        for (const requiredRight of request.requiredRights) {
          if (
            allRights.some(
              right => requiredRight === right || requiredRight.startsWith(right + ':')
            )
          ) {
            hasRightCount++;
          }
        }

        if (hasRightCount == request.requiredRights.length) {
          eligibleUsers.push({
            name: decodedToken.userName,
            userId: decodedToken.userId,
            cardHash: null, // No card hash for company and private devices
          });
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }

  return eligibleUsers;
};

// Builds the toast message for a failed retry of a stored request. A retry that fails
// because the authenticating user lacks the required right needs a different message than
// a technical failure, otherwise staff keep retrying with the same user.
const buildRetryFailureMessage = error => {
  const storedHint = 'Die Anfrage bleibt unter "Gespeicherte Anfragen" gespeichert.';

  if (error?.code === 'INSUFFICIENT_RIGHTS' || error?.data?.code === 'INSUFFICIENT_RIGHTS') {
    const requiredRights = error?.data?.requiredRights || error?.data?.requiredRight || [];
    const rightsList = (Array.isArray(requiredRights) ? requiredRights : [requiredRights])
      .filter(Boolean)
      .join(', ');

    return rightsList
      ? `Auch der bestätigende Benutzer hat nicht das benötigte Recht (${rightsList}). ${storedHint}`
      : `Auch der bestätigende Benutzer hat nicht die benötigten Rechte. ${storedHint}`;
  }

  return `Anfrage fehlgeschlagen. ${storedHint}`;
};

const calculateRemainingTime = expirationTime => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  const remainingDuration = adjExpirationTime - currentTime;

  return remainingDuration;
};

const retrieveStoredTokens = () => {
  const storedToken = localStorage.getItem('token');
  const storedExpirationDate = localStorage.getItem('expirationTime');
  const storedDeviceToken = localStorage.getItem('deviceToken');
  const isPrivateDevice = localStorage.getItem('isPrivateDevice') === 'true';
  const storedIntermediateTokens = localStorage.getItem('intermediateTokens');
  const storedRequestDeviceToken = localStorage.getItem(requestDeviceTokenStorageKey);

  const normalizeRequiredRights = value => {
    if (!value) return [];
    if (Array.isArray(value))
      return value
        .filter(Boolean)
        .map(v => String(v).trim())
        .filter(Boolean);
    if (typeof value === 'string') {
      const s = value.trim();
      return s ? [s] : [];
    }
    return [String(value).trim()].filter(Boolean);
  };

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= 3600) {
    localStorage.removeItem('token');
    localStorage.removeItem('expirationTime');
  }

  // validate and clean up intermediate tokens
  let validIntermediateTokens = {};
  if (storedIntermediateTokens) {
    try {
      const tokens = JSON.parse(storedIntermediateTokens);
      const currentTime = new Date().getTime();

      for (const [cardHash, tokenData] of Object.entries(tokens)) {
        if (currentTime < tokenData.expiration) {
          validIntermediateTokens[cardHash] = tokenData;
        }
      }

      // Update localStorage with cleaned tokens
      localStorage.setItem('intermediateTokens', JSON.stringify(validIntermediateTokens));
    } catch (error) {
      console.error('Error parsing intermediate tokens:', error);
      localStorage.removeItem('intermediateTokens');
    }
  }

  // Load and clean up stored requests
  let validStoredRequests = [];
  try {
    const storedRequestsData = localStorage.getItem('storedRequests');
    if (storedRequestsData) {
      const parsedRequests = JSON.parse(storedRequestsData);

      // Clean up expired requests immediately when loading
      const now = new Date().getTime();
      validStoredRequests = parsedRequests
        .filter(request => {
          if (!request.expirationTime) {
            // If no expiration time, keep the request (backward compatibility)
            return true;
          }

          const expirationTime = new Date(request.expirationTime).getTime();
          return now < expirationTime;
        })
        .map(request => {
          const requiredRights = normalizeRequiredRights(
            request?.requiredRights ??
              request?.requiredRight ??
              request?.missingRights ??
              request?.missingRight
          );
          return { ...request, requiredRights };
        });

      // Update localStorage with cleaned requests
      localStorage.setItem('storedRequests', JSON.stringify(validStoredRequests));
    }
  } catch (error) {
    console.error('Error loading stored requests:', error);
    localStorage.removeItem('storedRequests');
  }

  return {
    token: remainingTime > 3600 ? storedToken : null,
    duration: remainingTime > 3600 ? remainingTime : null,
    deviceToken: storedDeviceToken || '',
    requestDeviceToken: storedRequestDeviceToken || '',
    isPrivateDevice: isPrivateDevice,
    intermediateTokens: validIntermediateTokens,
    sessionTimeRemaining: 600,
    storedRequests: validStoredRequests,
  };
};

export const AuthContextProvider = props => {
  const tokenData = retrieveStoredTokens();
  const { rfidLogin: rfidLoginApi, getUserPreferences, getMenuCapabilities } = useUser();

  // wrapper to ensure function reference is initialized before useHttp executes
  function addIntermediateTokenWrapper(cardId, token) {
    addIntermediateTokenHandler(cardId, token);
  }

  const { sendRequest } = useHttp({ addIntermediateToken: addIntermediateTokenWrapper });
  const countCtx = useContext(CountContext);
  const [token, setToken] = useState(tokenData.token);
  const [deviceToken, setDeviceToken] = useState(tokenData.deviceToken);
  const [requestDeviceToken, setRequestDeviceToken] = useState(tokenData.requestDeviceToken);
  const [isPrivateDevice, setIsPrivateDevice] = useState(tokenData.isPrivateDevice);
  const [tempToken, setTempToken] = useState(null);
  const [tempIntermediateToken, setTempIntermediateToken] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  // Initialize rights from stored token immediately to avoid transient redirects on refresh
  const initialDecodedRights = (() => {
    try {
      if (tokenData.token) {
        const decoded = jwt_decode(tokenData.token);
        return decoded.userRights || { standard: [], expanded: [] };
      }
    } catch (error) {
      console.error('Error decoding stored token for initial rights:', error);
    }
    return { standard: [], expanded: [] };
  })();
  const [userRights, setUserRights] = useState(initialDecodedRights.standard);
  const [expandedUserRights, setExpandedUserRights] = useState(initialDecodedRights.expanded);
  const [otherUsersRights, setOtherUsersRights] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [intermediateTokens, setIntermediateTokens] = useState(tokenData.intermediateTokens);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(tokenData.sessionTimeRemaining);
  const [isCounterDevice, setIsCounterDevice] = useState(false);
  const timerStartedRef = useRef(false);
  const deviceExchangeAttemptRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalConfig, setAuthModalConfig] = useState(null);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [storedRequests, setStoredRequests] = useState(tokenData.storedRequests);
  const [isShowingModalForNewRequest, setIsShowingModalForNewRequest] = useState(false);
  const [preferences, setPreferences] = useState({
    showSuccessToastsInDataViews: true,
    mailSidebarCountOnlyAssigned: false,
    showSidebarMenuFilter: true,
    internalVerticalShowDetails: true,
    golfVerticalShowResourceSwitchCards: true,
    golfVerticalShowStaggeredGroupCards: true,
    showWeekdayInBookingsView: true,
    showWeekdayInResourcesView: true,
    startTimePricePerPerson: true,
    onsiteQrDefaultMode: 'token',
    ctrlFRedirectTarget: 'off', // 'off' | 'sidebarMenu' | 'headerSearch'
    colorMode: 'dark', // 'light' | 'dark' | 'system'
    showMailDeliveryStep: true,
    hideMailSendBackgroundActionHint: false,
    pizzaPopupNotifications: true,
    disabledCounters: [], // sidebar counter keys the user has hidden
  });
  const [capabilities, setCapabilities] = useState({});
  const [failedLoginWarning, setFailedLoginWarning] = useState(null);
  const [hasBeenIntroducedTo, setHasBeenIntroducedTo] = useState(null);
  const [userPreferencesHydrated, setUserPreferencesHydrated] = useState(false);

  // Initialize currentUser from stored token immediately
  const [currentUser, setCurrentUser] = useState(() => {
    if (tokenData.token) {
      try {
        const decodedToken = jwt_decode(tokenData.token);
        return {
          userId: decodedToken.userId,
          userName: decodedToken.userName,
          roles: Array.isArray(decodedToken.roles) ? decodedToken.roles : [],
        };
      } catch (error) {
        console.error('Error decoding stored token for currentUser initialization:', error);
        return null;
      }
    }
    return null;
  });

  const userIsLoggedIn = !!token;

  const getAuthHeaders = useCallback(
    ({ withAuth = true, withDevice = false } = {}) => {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (withAuth && token) {
        headers['Authorization'] = 'Bearer ' + token;
      }
      if (withDevice && deviceToken) {
        headers['Device-Token'] = deviceToken;
      }
      return headers;
    },
    [token, deviceToken]
  );

  // Update currentUser when token changes
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwt_decode(token);
        setCurrentUser({
          userId: decodedToken.userId,
          userName: decodedToken.userName,
          roles: Array.isArray(decodedToken.roles) ? decodedToken.roles : [],
        });
      } catch (error) {
        console.error('Error decoding token for currentUser update:', error);
        setCurrentUser(null);
      }
    } else {
      setCurrentUser(null);
    }
  }, [token]);

  const logoutHandler = useCallback(() => {
    setToken(null);
    setTempToken(null);
    setCurrentUser(null);
    setUserRights([]);
    setExpandedUserRights([]);
    setHasBeenIntroducedTo(null);
    localStorage.removeItem('token');
    localStorage.removeItem('expirationTime');

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    if (sessionTimer) {
      clearInterval(sessionTimer);
      sessionTimer = null;
    }
    timerStartedRef.current = false;
  }, []);

  const endShiftHandler = useCallback(() => {
    // For counter devices: full logout plus remove current user's intermediate token
    setToken(null);
    setTempToken(null);
    setCurrentUser(null);
    setHasBeenIntroducedTo(null);

    // Remove the current user's intermediate token
    if (currentUser && currentUser.userId) {
      // Find and remove the intermediate token for the current user
      setIntermediateTokens(prev => {
        const newTokens = { ...prev };
        // Find the token that belongs to the current user
        for (const [cardHash, tokenData] of Object.entries(newTokens)) {
          try {
            const decodedToken = jwt_decode(tokenData.token);
            if (decodedToken.userId === currentUser.userId) {
              delete newTokens[cardHash];
              break;
            }
          } catch (error) {
            console.error('Error decoding intermediate token in endShiftHandler:', error);
          }
        }
        localStorage.setItem('intermediateTokens', JSON.stringify(newTokens));
        return newTokens;
      });
    }

    localStorage.removeItem('token');
    localStorage.removeItem('expirationTime');

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    if (sessionTimer) {
      clearInterval(sessionTimer);
      sessionTimer = null;
    }
    timerStartedRef.current = false;
  }, [currentUser]);

  const sessionTimeoutHandler = useCallback(() => {
    setToken(null);
    setCurrentUser(null);
    setHasBeenIntroducedTo(null);
    localStorage.removeItem('token');
    localStorage.removeItem('expirationTime');

    // don't clear deviceToken or privateDevice - they should persist!

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    if (sessionTimer) {
      clearInterval(sessionTimer);
      sessionTimer = null;
    }
    timerStartedRef.current = false;
  }, []);

  // Forget a device token the backend reported as no longer honourable, without
  // touching the remembered-private-device flag, so the device can be confirmed
  // or activated again instead of running with private rights forever.
  const clearDeviceTokenHandler = useCallback(() => {
    setDeviceToken('');
    localStorage.removeItem('deviceToken');
    setIsCounterDevice(false);
  }, []);

  const resetDeviceHandler = useCallback(() => {
    setToken(null);
    setTempToken(null);
    setCurrentUser(null);
    setHasBeenIntroducedTo(null);
    setDeviceToken('');
    setRequestDeviceToken('');
    setIsPrivateDevice(false);
    setIntermediateTokens({});
    setStoredRequests([]);
    deviceExchangeAttemptRef.current = null;

    localStorage.removeItem('token');
    localStorage.removeItem('expirationTime');
    localStorage.removeItem('deviceToken');
    localStorage.removeItem(requestDeviceTokenStorageKey);
    localStorage.removeItem('isPrivateDevice');
    localStorage.removeItem('intermediateTokens');
    localStorage.removeItem('storedRequests');

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    if (sessionTimer) {
      clearInterval(sessionTimer);
      sessionTimer = null;
    }
    timerStartedRef.current = false;
    setIsCounterDevice(false);
    setMenuItems([]);
    setShowAuthModal(false);
  }, []);

  const loginHandler = async (token, data) => {
    const decodedDeviceToken = data.deviceToken ? jwt_decode(data.deviceToken) : null;
    const decodedToken = jwt_decode(token);
    const expirationTime = new Date(decodedToken.exp * 1000);

    // Store failed login warning if present (shown after redirect by RootLayout)
    if (data.failedLoginWarning) {
      setFailedLoginWarning(data.failedLoginWarning);
    }

    setToken(token);
    setHasBeenIntroducedTo(null);

    // Keep rights in sync with token immediately to avoid transient renders with stale rights.
    setUserRights(decodedToken?.userRights?.standard || []);
    setExpandedUserRights(decodedToken?.userRights?.expanded || []);

    // Extract current user information from token
    setCurrentUser({
      userId: decodedToken.userId,
      userName: decodedToken.userName,
      roles: Array.isArray(decodedToken.roles) ? decodedToken.roles : [],
    });

    localStorage.setItem('token', token);
    localStorage.setItem('expirationTime', expirationTime);

    setTempToken(null);

    // Store device token if provided
    if (data && data.deviceToken) {
      setDeviceToken(data.deviceToken);
      localStorage.setItem('deviceToken', data.deviceToken);
      // A device type was activated, so this is no longer a remembered private
      // device — otherwise the stale flag keeps suppressing the type picker.
      setIsPrivateDevice(false);
      localStorage.removeItem('isPrivateDevice');
    } else if (data) {
      // The backend could not honour the device token this browser sent (expired,
      // malformed, or its registration revoked/deleted). It has to leave the
      // storage, not just the state: while it sits there it keeps being sent, keeps
      // the device type picker suppressed and blocks attemptDeviceExchange, which
      // only runs on a device without a device token.
      if (data.deviceTokenInvalid) {
        setDeviceToken('');
        localStorage.removeItem('deviceToken');
      }
      if (data.deviceToken === null) {
        // This is a remembered private device. The storage key goes too, so a
        // reload cannot resurrect a token the state just dropped.
        setIsPrivateDevice(true);
        setDeviceToken('');
        localStorage.removeItem('deviceToken');
        localStorage.setItem('isPrivateDevice', 'true');
      }
    }

    // Check if this is a counter device - either from deviceToken or from the main token
    const isCounter =
      (decodedDeviceToken && decodedDeviceToken.deviceType === 'counter') ||
      decodedToken.deviceType === 'counter';

    // store intermediate tokens for counter devices
    if (data && data.intermediateToken && isCounter) {
      const intermediateTokenDecoded = jwt_decode(data.intermediateToken);
      // Intermediate tokens should always have a cardId (already hashed from server)
      if (intermediateTokenDecoded.cardId) {
        await addIntermediateTokenHandler(intermediateTokenDecoded.cardId, data.intermediateToken);
      }
    }
    setIsCounterDevice(isCounter);

    // start session timer for counter devices
    if (isCounter) {
      startSessionTimer(SESSION_TIME_REMAINING);
    }

    const remainingTime = calculateRemainingTime(expirationTime);

    logoutTimer = setTimeout(logoutHandler, remainingTime);

    // Load menu capabilities (preferences + introductions load via token-scoped effect)
    try {
      const caps = await getMenuCapabilities(token);
      if (caps && caps.capabilities) {
        setCapabilities(caps.capabilities);
      } else {
        setCapabilities({});
      }
    } catch (e) {
      setCapabilities({});
    }
  };

  const addIntermediateTokenHandler = useCallback(async (cardId, intermediateToken) => {
    const decoded = jwt_decode(intermediateToken);
    const expiration = decoded.exp * 1000;
    const hashedKey = cardId;

    setIntermediateTokens(prev => {
      const newTokens = {
        ...prev,
        [hashedKey]: {
          token: intermediateToken,
          expiration: expiration,
        },
      };
      localStorage.setItem('intermediateTokens', JSON.stringify(newTokens));

      return newTokens;
    });
  }, []);

  const removeIntermediateTokenHandler = useCallback(async cardId => {
    const hashedKey = cardId;

    setIntermediateTokens(prev => {
      const newTokens = { ...prev };
      delete newTokens[hashedKey];
      localStorage.setItem('intermediateTokens', JSON.stringify(newTokens));

      return newTokens;
    });
  }, []);

  const updateOtherUsersRights = useCallback(() => {
    const rights = [];

    // Check intermediate tokens for users with rights
    if (intermediateTokens && Object.keys(intermediateTokens).length > 0) {
      for (const [cardHash, tokenData] of Object.entries(intermediateTokens)) {
        try {
          const decodedToken = jwt_decode(tokenData.token);

          // Add standard rights
          if (decodedToken.userRights?.standard) {
            rights.push(...decodedToken.userRights.standard);
          }

          // Add expanded rights
          if (decodedToken.userRights?.expanded) {
            rights.push(...decodedToken.userRights.expanded);
          }
        } catch (error) {
          console.error('Error decoding intermediate token:', error);
        }
      }
    }

    // Check device token owner
    if (deviceToken && false) {
      // TODO: remove this once we have a way to grant rights to the device token
      try {
        const decodedDeviceToken = jwt_decode(deviceToken);

        if (decodedDeviceToken.userName && decodedDeviceToken.userRights) {
          // Add standard rights
          if (decodedDeviceToken.userRights.standard) {
            rights.push(...decodedDeviceToken.userRights.standard);
          }

          // Add expanded rights
          if (decodedDeviceToken.userRights.expanded) {
            rights.push(...decodedDeviceToken.userRights.expanded);
          }
        }
      } catch (error) {
        console.error('Error decoding device token:', error);
      }
    }

    // Remove duplicates
    const uniqueRights = [...new Set(rights)];
    setOtherUsersRights(uniqueRights);
  }, [intermediateTokens, deviceToken]);

  const startSessionTimer = useCallback(
    initialTime => {
      if (timerStartedRef.current) {
        return;
      }

      // Clear existing timer
      if (sessionTimer) {
        clearInterval(sessionTimer);
        sessionTimer = null;
      }

      // Set initial time
      setSessionTimeRemaining(initialTime);

      timerStartedRef.current = true;

      // Start countdown timer
      sessionTimer = setInterval(() => {
        setSessionTimeRemaining(prev => {
          const newTime = prev - 1;

          if (newTime <= 0) {
            sessionTimeoutHandler();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    },
    [sessionTimeoutHandler]
  );

  const resetSessionTimerHandler = useCallback(() => {
    timerStartedRef.current = false;
    startSessionTimer(SESSION_TIME_REMAINING);
  }, [startSessionTimer]);

  const rfidLoginHandler = async (cardId, intermediateTokenData) => {
    try {
      const result = await rfidLoginApi(cardId, intermediateTokenData);

      // Login with the new token
      await loginHandler(result.token, result);

      return result;
    } catch (error) {
      throw error;
    }
  };

  const checkRightHandler = useCallback(
    right => {
      for (const userRight of userRights) {
        if (right == userRight || right.startsWith(userRight + ':')) {
          return true;
        }
      }

      return false;
    },
    [userRights]
  );

  const checkRightOnlyInExpanded = useCallback(
    right => {
      // Check if user has the right in standard rights
      const hasStandardRight = userRights.some(
        userRight => right === userRight || right.startsWith(userRight + ':')
      );

      // Check if user has the right in expanded rights
      const hasExpandedRight = expandedUserRights.some(
        expandedRight => right === expandedRight || right.startsWith(expandedRight + ':')
      );

      // If user has the right only in expanded rights (not in standard), return true
      if (hasExpandedRight && !hasStandardRight) {
        return true;
      }

      // Check if any other user has the right (standard or expanded) but current user doesn't
      const otherUserHasRight = otherUsersRights.some(
        otherRight => right === otherRight || right.startsWith(otherRight + ':')
      );

      // Return true if other user has the right but current user doesn't have it in standard rights
      return otherUserHasRight && !hasStandardRight;
    },
    [userRights, expandedUserRights, otherUsersRights]
  );

  const checkRightAny = useCallback(
    right => {
      // Check if current user has the right in standard rights
      const hasStandardRight = userRights.some(
        userRight => right === userRight || right.startsWith(userRight + ':')
      );

      // Check if current user has the right in expanded rights
      const hasExpandedRight = expandedUserRights.some(
        expandedRight => right === expandedRight || right.startsWith(expandedRight + ':')
      );

      // If current user has the right, return true
      if (hasStandardRight || hasExpandedRight) {
        return true;
      }

      // Check if any other user has the right using cached rights array
      const otherUserHasRight = otherUsersRights.some(
        otherRight => right === otherRight || right.startsWith(otherRight + ':')
      );

      return otherUserHasRight;
    },
    [userRights, expandedUserRights, otherUsersRights]
  );

  // Sidebar counters the user has effectively hidden: a counter is only treated
  // as deactivated while the user still holds the matching `deactivateCounter:<key>`
  // right, so revoking the right transparently brings the badge back. Keys in
  // NON_DEACTIVATABLE_COUNTER_KEYS are never hidden, regardless of stored
  // preferences or rights. The empty case reuses a shared Set so the sidebar isn't
  // re-rendered when nothing is hidden.
  const deactivatedCounterKeys = useMemo(() => {
    const disabled = preferences?.disabledCounters;
    if (!Array.isArray(disabled) || disabled.length === 0) return EMPTY_DEACTIVATED_COUNTER_KEYS;
    const set = new Set();
    for (const key of disabled) {
      if (typeof key !== 'string' || NON_DEACTIVATABLE_COUNTER_KEYS.has(key)) continue;
      if (checkRightHandler(`deactivateCounter:${key}`)) {
        set.add(key);
      }
    }
    return set.size === 0 ? EMPTY_DEACTIVATED_COUNTER_KEYS : set;
  }, [preferences?.disabledCounters, checkRightHandler]);

  const setTempTokenHandler = useCallback((token, intermediateToken = null) => {
    setTempToken(token);
    setTempIntermediateToken(intermediateToken);
  }, []);

  const clearTempTokenHandler = useCallback(() => {
    setTempToken(null);
    setTempIntermediateToken(null);
  }, []);

  // Persist (or clear) the temporary "device type request" token. The settings
  // flow calls this after a request is created so the device can later exchange
  // the token for a real device token once IT approves.
  const setRequestDeviceTokenHandler = useCallback(newToken => {
    if (newToken) {
      localStorage.setItem(requestDeviceTokenStorageKey, newToken);
      // A fresh request means there is something to activate again — drop the
      // "nothing to activate" sentinel so the triggers resume.
      if (deviceExchangeAttemptRef.current === 'settled') {
        deviceExchangeAttemptRef.current = false;
      }
      setRequestDeviceToken(newToken);
    } else {
      localStorage.removeItem(requestDeviceTokenStorageKey);
      setRequestDeviceToken('');
    }
  }, []);

  const removeStoredRequest = useCallback(
    requestId => {
      setStoredRequests(prev => {
        const newRequests = prev.filter(req => req.id !== requestId);
        localStorage.setItem('storedRequests', JSON.stringify(newRequests));
        return newRequests;
      });
    },
    [setStoredRequests]
  );

  const retryStoredRequestWithRequest = useCallback(
    async (
      request,
      cardId = null,
      username = null,
      password = null,
      additionalAuthToken = null
    ) => {
      if (!request) {
        throw new Error('Request not found');
      }

      // Prepare headers with additional authentication data
      let retryHeaders = { ...request.headers };
      if (cardId) {
        // cardId hashen, bevor auf intermediateTokens zugegriffen wird
        const hashedCardId = await hashCardId(cardId);
        retryHeaders['Card-ID'] = cardId;
        // Intermediate Token für cardId suchen und mitschicken
        if (
          intermediateTokens &&
          intermediateTokens[hashedCardId] &&
          intermediateTokens[hashedCardId].token
        ) {
          retryHeaders['Intermediate-Token'] = intermediateTokens[hashedCardId].token;
        }
      }
      if (username && password) {
        // Use separate headers for username and base64-encoded password
        retryHeaders['Additional-Username'] = username;
        retryHeaders['Additional-Password'] = btoa(password);
      }
      if (additionalAuthToken) {
        retryHeaders['Additional-Auth-Token'] = additionalAuthToken;
      }

      const retryConfig = {
        url: request.url,
        method: request.method,
        headers: retryHeaders,
        body: request.body,
        params: request.params,
        returnBlob: request.returnBlob,
        noJSON: request.noJSON,
        // A retry must never be queued again: the request is already stored and the
        // caller needs a thrown error to keep it there instead of a silent null.
        noAutoStore: true,
      };

      // Make the request using useHttp
      try {
        const result = await sendRequest(retryConfig);
        return result;
      } catch (error) {
        throw error;
      }
    },
    [intermediateTokens]
  );

  const addStoredRequest = useCallback(
    (
      request,
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      const requestWithId = {
        ...request,
        id: Date.now() + Math.random(), // Simple unique ID
        timestamp: new Date().toISOString(),
        expirationTime: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 60 minutes
        toastMessage:
          toastMessage ||
          'Deine Anfrage wurde gespeichert und kann innerhalb einer Stunde in den offenen Anfragen bestätigt werden',
        displayInfo: displayInfo || null,
        onStored: onStored || null,
        onSuccess: onSuccess || null,
        successToastMessage: successToastMessage || null,
      };

      // Required rights arrive as a new array per response, so they have to be compared
      // by value - a reference comparison would never match an already stored request.
      const serializeRequiredRights = value => {
        if (!value) return '';
        const list = Array.isArray(value) ? value : [value];
        return list
          .map(entry => String(entry))
          .sort()
          .join('|');
      };

      // Check for duplicate requests (same URL, method, body, params)
      // Also check currentRequest since state updates are asynchronous
      const checkForDuplicate = requestToCheck => {
        if (!requestToCheck) return false;

        // Compare the core request properties with early returns
        if (requestToCheck.url !== request.url) return false;
        if ((requestToCheck.method || 'GET') !== (request.method || 'GET')) return false;
        if (JSON.stringify(requestToCheck.body) !== JSON.stringify(request.body)) return false;
        if (JSON.stringify(requestToCheck.params) !== JSON.stringify(request.params)) return false;
        if (
          serializeRequiredRights(requestToCheck.requiredRights) !==
          serializeRequiredRights(request.requiredRights)
        ) {
          return false;
        }

        // If we reach here, all properties match
        return true;
      };

      // Get current stored requests from localStorage to handle async state updates
      let currentStoredRequests = storedRequests;
      try {
        const localStorageData = localStorage.getItem('storedRequests');
        if (localStorageData) {
          const parsedRequests = JSON.parse(localStorageData);
          // Use the longer array to ensure we don't miss any
          if (parsedRequests.length > storedRequests.length) {
            currentStoredRequests = parsedRequests;
          }
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      }

      // Check both stored requests and current request
      const existingDuplicateIndex = currentStoredRequests.findIndex(checkForDuplicate);
      const isDuplicate = existingDuplicateIndex !== -1 || checkForDuplicate(currentRequest);

      // If it's a duplicate, replace the existing one instead of skipping
      if (isDuplicate && existingDuplicateIndex !== -1) {
        console.log('Duplicate request detected, replacing existing:', request.url);

        // Remove the existing duplicate
        setStoredRequests(prev => {
          const newRequests = [...prev];
          newRequests.splice(existingDuplicateIndex, 1);
          localStorage.setItem('storedRequests', JSON.stringify(newRequests));
          return newRequests;
        });
      } else if (isDuplicate) {
        return null;
      }

      // Store immediately in localStorage and state
      setStoredRequests(prev => {
        const newRequests = [...prev, requestWithId];
        localStorage.setItem('storedRequests', JSON.stringify(newRequests));
        return newRequests;
      });

      // Store temporarily for immediate access
      setCurrentRequest(requestWithId);

      // Call onStored callback if provided
      if (onStored) {
        onStored(requestWithId);
      }

      // Immediately show auth modal for the new request
      setIsShowingModalForNewRequest(true);

      // Get eligible users for this request
      const eligibleUsers = calculateEligibleUsers(
        requestWithId,
        intermediateTokens,
        deviceToken,
        token,
        isCounterDevice
      );

      setAuthModalConfig({
        requiredRights: requestWithId.requiredRights,
        eligibleUsers: eligibleUsers,
        displayInfo: requestWithId.displayInfo, // Pass displayInfo to the modal
        onAuthenticationSuccess: async (
          cardId = null,
          username = null,
          password = null,
          additionalAuthToken = null
        ) => {
          setShowAuthModal(false);

          try {
            const result = await retryStoredRequestWithRequest(
              requestWithId,
              cardId,
              username,
              password,
              additionalAuthToken
            );

            // If we reach here, the request was successful (the retry throws on failure)
            setCurrentRequest(null);
            removeStoredRequest(requestWithId.id);

            // Show generic success message for retried requests
            toast.success(requestWithId.successToastMessage || 'Anfrage erfolgreich ausgeführt');

            // Call onStored callback with the result if provided
            if (requestWithId.onSuccess) {
              requestWithId.onSuccess(result);
            } else if (requestWithId.onStored) {
              requestWithId.onStored(result);
            }

            setIsShowingModalForNewRequest(false);
            return result;
          } catch (error) {
            console.error('Request retry failed:', error);
            toast.error(buildRetryFailureMessage(error));

            // Clear the temporary request
            setCurrentRequest(null);

            // Keep the request in localStorage since authentication failed
            // (it's already there from the initial storage)

            setIsShowingModalForNewRequest(false);
            throw error;
          }
        },
        onCancel: () => {
          setShowAuthModal(false);
          // Reset the flag when modal is cancelled
          if (isShowingModalForNewRequest) {
            setIsShowingModalForNewRequest(false);
          }
          // Clear the current request if it exists
          setCurrentRequest(null);
          // Show toast when auth modal is cancelled
          if (toastMessage) {
            toast.info(toastMessage);
          }
        },
      });
      setShowAuthModal(true);

      return requestWithId.id; // Return the generated ID
    },
    [
      storedRequests,
      currentRequest,
      setStoredRequests,
      intermediateTokens,
      deviceToken,
      token,
      isCounterDevice,
      retryStoredRequestWithRequest,
      removeStoredRequest,
    ]
  );

  const clearAllStoredRequests = useCallback(() => {
    setStoredRequests([]);
    localStorage.removeItem('storedRequests');
  }, [setStoredRequests]);

  const getStoredRequest = useCallback(
    requestId => {
      // First check stored requests
      const storedRequest = storedRequests.find(req => req.id === requestId);
      if (storedRequest) {
        return storedRequest;
      }

      // Then check current request
      if (currentRequest && currentRequest.id === requestId) {
        return currentRequest;
      }

      return null;
    },
    [storedRequests, currentRequest]
  );

  const retryStoredRequest = useCallback(
    async (
      requestId,
      cardId = null,
      username = null,
      password = null,
      additionalAuthToken = null
    ) => {
      const request = getStoredRequest(requestId);
      if (!request) {
        throw new Error('Request not found');
      }

      return retryStoredRequestWithRequest(
        request,
        cardId,
        username,
        password,
        additionalAuthToken
      );
    },
    [retryStoredRequestWithRequest, getStoredRequest]
  );

  const showAuthModalForRequest = useCallback(
    (requestId, onRetrySuccess, requestData = null, onCancel = null) => {
      // Use provided request data or try to find it in state
      let request = requestData;
      if (!request) {
        request = getStoredRequest(requestId);
      }

      if (!request) {
        return;
      }

      // Get eligible users for this request
      const eligibleUsers = calculateEligibleUsers(
        request,
        intermediateTokens,
        deviceToken,
        token,
        isCounterDevice
      );

      setAuthModalConfig({
        requiredRights: request.requiredRights,
        eligibleUsers: eligibleUsers,
        displayInfo: request.displayInfo, // Pass displayInfo to the modal
        onAuthenticationSuccess: async (
          cardId = null,
          username = null,
          password = null,
          additionalAuthToken = null
        ) => {
          setShowAuthModal(false);

          try {
            const result = await retryStoredRequestWithRequest(
              request,
              cardId,
              username,
              password,
              additionalAuthToken
            );

            // If we reach here, the request was successful (the retry throws on failure)
            removeStoredRequest(request.id);

            // Show generic success message for retried requests
            toast.success(request.successToastMessage || 'Anfrage erfolgreich ausgeführt');

            // Call the original onStored callback with the result if provided
            if (request.onStored) {
              request.onStored(result);
            }

            // Call the provided onRetrySuccess callback if available
            if (onRetrySuccess) {
              await onRetrySuccess(requestId, cardId, username, password, additionalAuthToken);
            }

            return result;
          } catch (error) {
            console.error('Request retry failed:', error);
            toast.error(buildRetryFailureMessage(error));
            throw error;
          }
        },
        onCancel: () => {
          setShowAuthModal(false);
          // Reset the flag when modal is cancelled
          if (isShowingModalForNewRequest) {
            setIsShowingModalForNewRequest(false);
          }
          // Clear the current request if it exists
          setCurrentRequest(null);
          // Call the provided onCancel callback if available
          if (onCancel) {
            onCancel();
          }
        },
      });
      setShowAuthModal(true);
    },
    [
      deviceToken,
      intermediateTokens,
      isShowingModalForNewRequest,
      getStoredRequest,
      token,
      isCounterDevice,
    ]
  );

  const cleanupExpiredRequests = useCallback(() => {
    const now = new Date().getTime();

    setStoredRequests(prev => {
      const validRequests = prev.filter(request => {
        if (!request.expirationTime) {
          // If no expiration time, keep the request (backward compatibility)
          return true;
        }

        const expirationTime = new Date(request.expirationTime).getTime();
        return now < expirationTime;
      });

      // Update localStorage
      localStorage.setItem('storedRequests', JSON.stringify(validRequests));

      return validRequests;
    });
  }, [setStoredRequests]);

  useEffect(() => {
    if (tokenData.token && tokenData.duration) {
      logoutTimer = setTimeout(logoutHandler, tokenData.duration);
    }

    let isCounter = false;
    if (tokenData.token) {
      try {
        const decodedToken = jwt_decode(tokenData.token);
        isCounter = decodedToken.deviceType === 'counter';

        // Start session timer if counter device
        if (isCounter) {
          startSessionTimer(SESSION_TIME_REMAINING);
        }
      } catch (error) {
        console.error('Error decoding stored token:', error);
      }
    }

    // If no token but we have a device token, this is likely a counter device
    if (!tokenData.token && tokenData.deviceToken) {
      isCounter = true;
    }

    setIsCounterDevice(isCounter);
    setIsPrivateDevice(tokenData.isPrivateDevice);

    // Mark as initialized after first render
    setIsInitialized(true);
  }, []); // Remove dependencies that cause infinite loops

  useEffect(() => {
    let cancelled = false;
    const loadPreferencesForToken = async () => {
      if (!token) {
        setUserPreferencesHydrated(true);
        return;
      }
      setUserPreferencesHydrated(false);
      try {
        const userPrefs = await getUserPreferences(token);
        if (cancelled) return;
        if (userPrefs?.preferences) {
          setPreferences(prev => ({ ...prev, ...userPrefs.preferences }));
        }
        if (Array.isArray(userPrefs?.hasBeenIntroducedTo)) {
          setHasBeenIntroducedTo(userPrefs.hasBeenIntroducedTo);
        } else if (userPrefs) {
          setHasBeenIntroducedTo([]);
        }
      } catch (_e) {
      } finally {
        if (!cancelled) {
          setUserPreferencesHydrated(true);
        }
      }
    };
    loadPreferencesForToken();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getUserPreferences identity changes each render from useUser
  }, [token]);

  // Load menu capabilities for the active token, including Super-Admin token swaps.
  useEffect(() => {
    let cancelled = false;
    const fetchCaps = async () => {
      if (!token) {
        setCapabilities({});
        return;
      }
      try {
        const caps = await getMenuCapabilities(token);
        if (cancelled) return;
        if (caps?.capabilities) {
          setCapabilities(caps.capabilities);
        } else {
          setCapabilities({});
        }
      } catch (_e) {
        if (!cancelled) {
          setCapabilities({});
        }
      }
    };
    fetchCaps();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getMenuCapabilities identity changes each render from useUser
  }, [token]);

  // Update stored requests count - consolidated into a single effect
  useEffect(() => {
    if (countCtx.setCount && isInitialized) {
      countCtx.setCount('storedRequests', storedRequests.length);
    }
  }, [countCtx.setCount, isInitialized, storedRequests.length]);

  // Ensure stored requests are properly persisted to localStorage on initialization
  useEffect(() => {
    if (isInitialized && storedRequests.length > 0) {
      localStorage.setItem('storedRequests', JSON.stringify(storedRequests));
    }
  }, [isInitialized, storedRequests.length]);

  // Ensure stored requests are saved to localStorage when loaded from initialization
  useEffect(() => {
    if (tokenData.storedRequests && tokenData.storedRequests.length > 0) {
      localStorage.setItem('storedRequests', JSON.stringify(tokenData.storedRequests));
    }
  }, []);

  // Add global click listener to reset timer on user activity
  useEffect(() => {
    if (!isCounterDevice || !userIsLoggedIn) {
      return;
    }

    const handleUserActivity = () => {
      resetSessionTimerHandler();
    };

    // Add event listeners for user activity
    document.addEventListener('click', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);

    // Cleanup event listeners
    return () => {
      document.removeEventListener('click', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
    };
  }, [isCounterDevice, userIsLoggedIn, resetSessionTimerHandler]);

  // Update other users' rights when intermediate tokens or device token changes
  useEffect(() => {
    updateOtherUsersRights();
  }, [intermediateTokens, deviceToken]);

  useEffect(() => {
    if (token) {
      const decodedToken = jwt_decode(token);
      const userRights = decodedToken.userRights;
      setUserRights(userRights.standard);
      setExpandedUserRights(userRights.expanded);
      let menuItemsPre = [];

      // build up menuItem Array
      for (const item of availableMenuItems) {
        if (item.devOnly && !isDevEnvironment) {
          continue;
        }
        if (SUPPRESSED_MENU_LINKS.has(item.link)) {
          continue;
        }
        let hasStandardRight = false;
        let hasExpandedRight = false;
        const passesVisibility = !item.visibleIf || item.visibleIf({ capabilities });

        if (!item.needsRights) {
          hasStandardRight = true;
        } else if (item.needsAllRights) {
          // User must have every listed right (each need matched by some user right)
          const allStandard = item.needsRights.every(need =>
            userRights.standard.some(
              ur => ur === need || need.startsWith(ur + ':') || ur.startsWith(need + ':')
            )
          );
          const allExpanded = item.needsRights.every(need =>
            userRights.expanded.some(
              ur => ur === need || need.startsWith(ur + ':') || ur.startsWith(need + ':')
            )
          );
          if (allStandard) hasStandardRight = true;
          if (allExpanded) hasExpandedRight = true;
        } else {
          // Check if user has standard rights for this item (any match)
          for (const userRight of userRights.standard) {
            if (
              item.needsRights.includes(userRight) ||
              item.needsRights.some(str => str.startsWith(userRight + ':'))
            ) {
              hasStandardRight = true;
              break;
            }
          }

          // Check if user has extended rights for this item
          for (const userRight of userRights.expanded) {
            if (
              item.needsRights.includes(userRight) ||
              item.needsRights.some(str => str.startsWith(userRight + ':'))
            ) {
              hasExpandedRight = true;
              break;
            }
          }
        }

        // Only add to menu if user has standard rights and passes visibility
        if (hasStandardRight && passesVisibility) {
          // Special handling for stored requests - only show if there are actual requests
          if (item.countKey === 'storedRequests') {
            // Use storedRequests state directly for menu visibility
            if (storedRequests.length > 0) {
              if (!menuItemsPre.includes(item)) {
                menuItemsPre = [...menuItemsPre, item];
              }
            }
          } else {
            if (!menuItemsPre.includes(item)) {
              menuItemsPre = [...menuItemsPre, item];
            }
          }
        } else if (hasExpandedRight && passesVisibility) {
          item.isExpanded = true;
          menuItemsPre = [...menuItemsPre, item];
        }
      }
      setMenuItems(menuItemsPre);
    } else {
      setMenuItems([]);
    }
  }, [token, storedRequests, capabilities]);

  // Device type request exchange: when the user is authenticated and this device
  // holds a temporary request token but no real device token yet, try to swap it.
  // If IT approved the request in the meantime, the backend returns a real device
  // token plus a fresh device-typed auth token, which we apply via loginHandler.
  // While the request is still pending we keep the token and retry later. Triggers:
  // auth/token change, and whenever the app regains focus/visibility (so approving
  // on another device and returning to this one activates it without a reload).
  const attemptDeviceExchange = useCallback(async () => {
    // In-flight guard; also holds the 'settled' sentinel once the backend
    // reported that this device has no request at all, so every window focus
    // does not fire another pointless exchange call.
    if (deviceExchangeAttemptRef.current) return;
    // Read everything from localStorage so this works regardless of closure
    // staleness (e.g. right after a re-login the state token may lag).
    const authToken = localStorage.getItem('token');
    // The claim ticket is optional: it lives in one origin's localStorage and is
    // regularly gone by the time IT approves, so the install id in the body is
    // what actually resolves this browser's request.
    const reqToken = localStorage.getItem(requestDeviceTokenStorageKey);
    if (!authToken) return;
    // Already upgraded to a real device token on this device.
    if (localStorage.getItem('deviceToken')) return;

    deviceExchangeAttemptRef.current = true;
    // Nothing left to activate on this device — stops the focus trigger from
    // asking again for the rest of the session.
    let settled = false;
    try {
      const descriptor = getDeviceDescriptor();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authToken,
      };
      if (reqToken) {
        headers['Device-Request-Token'] = reqToken;
      }
      const result = await sendRequest({
        url: '/api/v1/deviceTypeRequest/exchange',
        method: 'POST',
        headers,
        body: {
          installId: descriptor.installId,
          userAgent: descriptor.userAgent,
        },
      });

      if (!result) return;

      if (result.status === 'approved' && result.token) {
        localStorage.removeItem(requestDeviceTokenStorageKey);
        setRequestDeviceToken('');
        // The device is no longer a plain private device.
        setIsPrivateDevice(false);
        localStorage.removeItem('isPrivateDevice');
        await loginHandler(result.token, {
          deviceToken: result.deviceToken,
          intermediateToken: result.intermediateToken,
        });
        toast.success('Gerätetyp wurde freigegeben und für dieses Gerät aktiviert.');
      } else if (result.status === 'rejected') {
        localStorage.removeItem(requestDeviceTokenStorageKey);
        setRequestDeviceToken('');
        settled = true;
        toast.info(
          result.rejectionReason
            ? `Deine Gerätetyp-Anfrage wurde abgelehnt: ${result.rejectionReason}`
            : 'Deine Gerätetyp-Anfrage wurde abgelehnt.'
        );
      } else if (result.status === 'invalid') {
        localStorage.removeItem(requestDeviceTokenStorageKey);
        setRequestDeviceToken('');
        settled = true;
      }
      // 'pending' -> retry on the next trigger.
    } catch (_error) {
      // Network/other error: allow a retry on the next trigger.
    } finally {
      deviceExchangeAttemptRef.current = settled ? 'settled' : false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loginHandler identity changes each render
  }, [sendRequest]);

  // Keep a ref to the latest exchange callback so the trigger effects don't have
  // to depend on its (unstable) identity and re-run on every render.
  const attemptDeviceExchangeRef = useRef(attemptDeviceExchange);
  useEffect(() => {
    attemptDeviceExchangeRef.current = attemptDeviceExchange;
  }, [attemptDeviceExchange]);

  // Trigger on auth/token/request-token changes.
  useEffect(() => {
    if (!isInitialized) return;
    attemptDeviceExchangeRef.current?.();
  }, [isInitialized, token, requestDeviceToken, deviceToken]);

  // Retry when the app regains focus/visibility (e.g. after approving elsewhere).
  useEffect(() => {
    const onFocus = () => attemptDeviceExchangeRef.current?.();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Subscribe every sidebar count badge the user has rights for. Counts are
  // push-driven: countContext queues a key on registration and whenever the
  // backend sends a `counts:invalidate` over Socket.io, then fetches all queued
  // keys in ONE bundled request via the fetcher installed below. Registering
  // ~25 badges therefore costs a single round-trip instead of 25.
  useEffect(() => {
    if (!token || !isInitialized || !countCtx.registerCount || !countCtx.setBundledFetcher) return;

    const decodedToken = jwt_decode(token);
    const userRights = decodedToken.userRights;

    const keys = [];
    availableMenuItems.forEach(item => {
      if (item.devOnly && !isDevEnvironment) {
        return;
      }
      if (!item.countUrl || !item.countKey || !item.autoStartCount) return;

      let hasRight = false;
      for (const userRight of [...userRights.standard, ...userRights.expanded]) {
        if (
          !item.needsRights ||
          item.needsRights.includes(userRight) ||
          item.needsRights.some(str => str.startsWith(userRight + ':'))
        ) {
          hasRight = true;
          break;
        }
      }

      // Skip counters the user has hidden via settings — no need to fetch a
      // count that will never be shown. The sidebar render guard hides any value
      // that may already be cached, so re-enabling re-registers and refetches.
      if (hasRight && !deactivatedCounterKeys.has(item.countKey)) {
        if (!keys.includes(item.countKey)) keys.push(item.countKey);
      }
    });

    // Resolves the requested keys against the bundled endpoint. The backend
    // rights-checks each key individually and reports the ones it withheld, so
    // asking for the full set is safe.
    const fetchBundledCounts = async requestedKeys => {
      const params = new URLSearchParams({ keys: requestedKeys.join(',') });
      if (preferences?.mailSidebarCountOnlyAssigned === true) {
        params.set('mailsOnlyAssignedToMe', 'true');
      }
      const response = await sendRequest({
        url: `/api/v1/counts?${params.toString()}`,
        method: 'GET',
        headers: getAuthHeaders(),
        // Badge traffic: never drives the top request-activity bar.
        background: true,
      });
      return response?.counts || {};
    };

    countCtx.setBundledFetcher(fetchBundledCounts);
    keys.forEach(key => countCtx.registerCount(key));

    return () => {
      keys.forEach(key => countCtx.unregisterCount(key));
    };
  }, [isInitialized, token, preferences?.mailSidebarCountOnlyAssigned, deactivatedCounterKeys]);

  const contextValue = useMemo(
    () => ({
      token: token,
      deviceToken: deviceToken,
      requestDeviceToken: requestDeviceToken,
      setRequestDeviceToken: setRequestDeviceTokenHandler,
      checkDeviceActivation: attemptDeviceExchange,
      isPrivateDevice: isPrivateDevice,
      tempToken: tempToken,
      tempIntermediateToken: tempIntermediateToken,
      isLoggedIn: userIsLoggedIn,
      isInitialized: isInitialized,
      menuItems: menuItems,
      userRights: userRights,
      expandedUserRights: expandedUserRights,
      otherUsersRights: otherUsersRights,
      intermediateTokens: intermediateTokens,
      sessionTimeRemaining: sessionTimeRemaining,
      isCounterDevice: isCounterDevice,
      currentUser: currentUser,
      login: loginHandler,
      logout: logoutHandler,
      setTempToken: setTempTokenHandler,
      clearTempToken: clearTempTokenHandler,
      checkRight: checkRightHandler,
      checkRightOnlyInExpanded: checkRightOnlyInExpanded,
      checkRightAny: checkRightAny,
      deactivatedCounterKeys: deactivatedCounterKeys,
      addIntermediateToken: addIntermediateTokenHandler,
      removeIntermediateToken: removeIntermediateTokenHandler,
      resetSessionTimer: resetSessionTimerHandler,
      rfidLogin: rfidLoginHandler,
      showAuthModal: showAuthModal,
      authModalConfig: authModalConfig,
      setAuthModal: setShowAuthModal,
      setAuthModalConfig: setAuthModalConfig,
      storedRequests: storedRequests,
      setStoredRequests: setStoredRequests,
      addStoredRequest: addStoredRequest,
      removeStoredRequest: removeStoredRequest,
      clearAllStoredRequests: clearAllStoredRequests,
      getStoredRequest: getStoredRequest,
      showAuthModalForRequest: showAuthModalForRequest,
      retryStoredRequest: retryStoredRequest,
      retryStoredRequestWithRequest: retryStoredRequestWithRequest,
      getAuthHeaders: getAuthHeaders,
      cleanupExpiredRequests: cleanupExpiredRequests,
      endShiftHandler: endShiftHandler,
      resetDevice: resetDeviceHandler,
      clearDeviceToken: clearDeviceTokenHandler,
      preferences: preferences,
      setPreferences: setPreferences,
      hasBeenIntroducedTo: hasBeenIntroducedTo,
      setHasBeenIntroducedTo: setHasBeenIntroducedTo,
      userPreferencesHydrated,
      capabilities: capabilities,
      failedLoginWarning: failedLoginWarning,
      clearFailedLoginWarning: () => setFailedLoginWarning(null),
      refreshCountForMenuItem: menuItemName => {
        const menuItem = availableMenuItems.find(item => item.name === menuItemName);
        if (menuItem && menuItem.countKey && countCtx.refreshCount) {
          countCtx.refreshCount(menuItem.countKey);
        }
      },
    }),
    [
      token,
      deviceToken,
      requestDeviceToken,
      setRequestDeviceTokenHandler,
      attemptDeviceExchange,
      isPrivateDevice,
      tempToken,
      tempIntermediateToken,
      userIsLoggedIn,
      isInitialized,
      menuItems,
      userRights,
      expandedUserRights,
      otherUsersRights,
      intermediateTokens,
      sessionTimeRemaining,
      isCounterDevice,
      currentUser,
      loginHandler,
      logoutHandler,
      setTempTokenHandler,
      clearTempTokenHandler,
      checkRightHandler,
      checkRightOnlyInExpanded,
      checkRightAny,
      deactivatedCounterKeys,
      addIntermediateTokenHandler,
      removeIntermediateTokenHandler,
      resetSessionTimerHandler,
      rfidLoginHandler,
      showAuthModal,
      authModalConfig,
      setShowAuthModal,
      setAuthModalConfig,
      storedRequests,
      setStoredRequests,
      addStoredRequest,
      removeStoredRequest,
      clearAllStoredRequests,
      getStoredRequest,
      showAuthModalForRequest,
      retryStoredRequest,
      retryStoredRequestWithRequest,
      getAuthHeaders,
      cleanupExpiredRequests,
      endShiftHandler,
      resetDeviceHandler,
      clearDeviceTokenHandler,
      preferences,
      hasBeenIntroducedTo,
      userPreferencesHydrated,
      capabilities,
      failedLoginWarning,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext;
